/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { BsFillArrowRightSquareFill } from 'react-icons/bs';
import { FaArrowAltCircleRight, FaLock } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

import Download from '../../assets/imgs/bt_download.svg';
import MedalOff from '../../assets/imgs/medalhaOff.png';
import MedalOn from '../../assets/imgs/medalhaOn.png';
import { About } from '../../components/About';
import { Certificate } from '../../components/Certificate';
import { Loading } from '../../components/Loading';
import { NoData } from '../../components/NoData';
import { UpdateProfileModal } from '../../components/UpdateProfileModal';
import { Course } from '../../types/course/Course';
import { CertificationLog } from '../../types/my-certifications/MyCertification';
import { validateUserProfile } from '../../utils/validateUserProfile';
import { useLazyFetchCurrentUserQuery } from '../auth/authApiSlice';
import { selectToken, selectUser, setCredentials } from '../auth/authSlice';
import { statusTranslate } from '../exam/helpers/statusTranslate';
import { EmailConfirmationGate } from '../exam/ui/EmailConfirmationGate';
import {
  useGetCourseMutation,
  useGetGlobalQuery,
  useGetMyCertificationsQuery,
  useGetServerTimeQuery,
} from './myCertificationsApiSlice';

const getCertificationType = {
  first_certification: 'Primeira certificação',
  renovation: 'Renovação',
};

const url = process.env.REACT_APP_COURSE_URL;

export function MyCertifications() {
  const user = useSelector(selectUser);
  const token = useSelector(selectToken);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [log, setLog] = useState<CertificationLog | null>();
  const [isRenew, setIsRenew] = useState(false);
  const [course, setCourse] = useState<Course[]>([]);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showEmailGateModal, setShowEmailGateModal] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  const [getCourse] = useGetCourseMutation();
  const [fetchCurrentUser] = useLazyFetchCurrentUserQuery();
  const { data: globalData } = useGetGlobalQuery({ apiKey: user?.apiKey }, {});

  const {
    data: certifications,
    isFetching,
    isLoading,
  } = useGetMyCertificationsQuery(user!._id, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const { data: serverTime } = useGetServerTimeQuery(null, {
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  async function getCourseData(id: string) {
    const resp = await getCourse(id);
    if (resp && 'data' in resp) {
      const _course = resp.data;

      setCourse([...course, _course]);
    }
  }

  function isProfileComplete(showModal = false) {
    return validateUserProfile(
      user,
      showModal ? () => setShowProfileModal(true) : undefined,
    );
  }

  function proceedToExam(certificationId: string) {
    if (isProfileComplete()) {
      navigate(`/exam/${certificationId}`);
    } else {
      setShowProfileModal(true);
    }
  }

  async function handleStartExam(certificationId: string) {
    // Email confirmation is only re-checked when the local store still believes
    // the user is unconfirmed. The backend does not push the confirmation back,
    // so we re-fetch the player on demand to avoid blocking an already-confirmed
    // candidate with stale persisted state.
    if (user?.extra?.emailConfirmed !== false) {
      proceedToExam(certificationId);
      return;
    }

    setIsCheckingEmail(true);
    try {
      const freshUser = await fetchCurrentUser().unwrap();
      if (token) {
        dispatch(
          setCredentials({
            token,
            user: {
              _id: freshUser._id,
              email: freshUser.email,
              name: freshUser.name,
              image: freshUser.image,
              extra: freshUser.extra,
              apiKey: user?.apiKey,
            },
          }),
        );
      }

      if (freshUser.extra?.emailConfirmed === false) {
        setShowEmailGateModal(true);
        return;
      }

      proceedToExam(certificationId);
    } catch {
      // If the re-check fails, keep the gate closed-fast behavior: surface the
      // confirmation modal rather than letting an unconfirmed user through.
      setShowEmailGateModal(true);
    } finally {
      setIsCheckingEmail(false);
    }
  }

  useEffect(() => {
    const hasRenew = certifications?.some(
      item => item.certification.certificate_type === 'renovation',
    );

    if (hasRenew) {
      setIsRenew(true);
    }

    certifications?.forEach(item => {
      if (
        item?.certification?.crcp &&
        !!item?.extra?.hasCourse &&
        !item?.extra?.completedCourse
      ) {
        getCourseData(item.certification.crcp);
      }
    });

    isProfileComplete(true);
  }, [certifications, user]);

  function handleDate(created: number, mouth: number) {
    const originalDate = new Date(created);
    const newDate = new Date(originalDate);
    newDate.setMonth(newDate.getMonth() + mouth);

    return newDate.toLocaleDateString('pt-BR');
  }

  function verifyValidate(created: number, month: number) {
    const originalDate = new Date(created);
    const newDate = new Date(originalDate);
    newDate.setMonth(newDate.getMonth() + month);

    const expiredDate = newDate.getTime();

    const _serverTime = serverTime?.[0]?.server_time;
    const _serverTimeDate = _serverTime ? new Date(_serverTime) : null;

    if (_serverTimeDate && _serverTimeDate.getTime() >= expiredDate)
      return false;

    return true;
  }

  function fixedNumber(number: number) {
    return number.toFixed(2);
  }

  function handleStatus(item: CertificationLog) {
    const score = fixedNumber(Number(item.quiz_log?.percent) * 100);

    if (item.status === '3' && item?.shouldUpdate) {
      return (
        <Link to="/profile" className="block">
          <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center">
            <div className="min-h-[104px] min-w-0 flex-1 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-amber-800 shadow-sm transition hover:border-amber-300">
              <p className="text-lg font-bold tracking-wide hover:underline">
                EM APROVAÇÃO
              </p>
              <p className="mt-1 flex items-center text-sm font-semibold hover:underline">
                <FaArrowAltCircleRight className="mr-2" size={18} /> Aguardando
                documentação:
                {item?.approval && item.approval.length > 0
                  ? item.approval[item.approval.length - 1].comment
                  : null}
              </p>
              <p className="mt-2 text-sm font-medium text-amber-900 hover:underline">
                Clique aqui para atualizar a documentação
              </p>
            </div>

            <div className="relative mx-auto shrink-0 sm:mx-0">
              <img
                className="h-24 w-24 grayscale sm:h-28 sm:w-28"
                src={MedalOn}
                alt="Medalha"
              />
              <p className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xl font-extrabold text-[#020817]">
                {score}
              </p>
            </div>
          </div>
        </Link>
      );
    }

    if (item.status === '3') {
      return (
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center">
          <div className="min-h-[104px] min-w-0 flex-1 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-amber-800 shadow-sm">
            <p className="text-lg font-bold tracking-wide">EM APROVAÇÃO</p>
            <p className="mt-1 flex items-center text-sm font-semibold">
              <FaArrowAltCircleRight className="mr-2" size={20} /> Aguardando
              aprovação
            </p>
          </div>

          <div className="relative mx-auto shrink-0 sm:mx-0">
            <img
              className="h-24 w-24 grayscale sm:h-28 sm:w-28"
              src={MedalOn}
              alt="Medalha"
            />
            <p className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xl font-extrabold text-[#020817]">
              {score}
            </p>
          </div>
        </div>
      );
    }

    if (item.status === '5') {
      return (
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex min-h-[104px] min-w-0 flex-1 items-center justify-center rounded-2xl border border-rose-200 bg-rose-50 px-6 py-5 text-rose-700 shadow-sm">
            <p className="text-lg font-extrabold tracking-wide">REPROVADO</p>
          </div>

          <div className="relative mx-auto shrink-0 sm:mx-0">
            <img
              className="h-24 w-24 sm:h-28 sm:w-28"
              src={MedalOff}
              alt="Medalha"
            />
            <p className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xl font-extrabold text-[#020817]">
              {score}
            </p>
          </div>
        </div>
      );
    }

    if (item.status === '4') {
      return (
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center">
          <div className="min-h-[104px] min-w-0 flex-1 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-emerald-800 shadow-sm">
            <p className="text-lg font-bold tracking-wide">APROVADO</p>
            <button
              type="button"
              className="mt-1 flex items-center text-sm font-semibold hover:underline"
              onClick={() => setLog(item)}
            >
              <FaArrowAltCircleRight className="mr-2" size={18} /> Verificar meu
              certificado
            </button>
          </div>

          <div className="relative mx-auto shrink-0 sm:mx-0">
            <img
              className="h-24 w-24 sm:h-28 sm:w-28"
              src={MedalOn}
              alt="Medalha"
            />
            <p className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-xl font-extrabold text-[#020817]">
              {score}
            </p>
          </div>
        </div>
      );
    }

    if (item.status === '11') {
      return (
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex min-h-[104px] min-w-0 flex-1 items-center justify-center rounded-2xl border border-slate-300 bg-slate-100 px-6 py-5 text-slate-700 shadow-sm">
            <p className="text-lg font-extrabold tracking-wide">SUSPENSO</p>
          </div>
        </div>
      );
    }

    if (item.status === '10') {
      return (
        <div className="flex w-full justify-end">
          <button
            type="button"
            className="cursor-default rounded-2xl border border-gray-400 bg-gray-500 px-4 py-3 text-sm font-bold uppercase tracking-wide text-white"
          >
            CERTIFICADO VENCIDO
          </button>
        </div>
      );
    }

    if (
      item.status === '9' ||
      !verifyValidate(item.created, item.certification?.voucher_validity)
    ) {
      return (
        <div className="flex w-full justify-end">
          <button
            type="button"
            className="cursor-default rounded-2xl border border-[#001FA8] bg-[#000097] px-4 py-3 text-sm font-bold uppercase tracking-wide text-[#F4F7FD]"
          >
            INSCRIÇÃO VENCIDA
          </button>
        </div>
      );
    }

    return (
      <button
        type="button"
        className="cursor-default rounded-xl border border-[#001FA8] bg-[#000097] px-3 py-2 text-xs font-semibold uppercase tracking-wide text-[#F4F7FD]"
      >
        {statusTranslate[item.status]}
      </button>
    );
  }

  function handleGoingStatus(item: CertificationLog) {
    if (item.status === '1') {
      if (
        item &&
        item.extra &&
        item.extra?.hasCourse &&
        item.extra.completedCourse === false
      ) {
        return (
          <div className="flex w-full items-center justify-between gap-4 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-rose-700 shadow-sm">
            <p className="text-sm font-bold leading-tight sm:text-base">
              COMPLETE O CURSO PARA REALIZAR A PROVA
            </p>

            <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#991B1B] text-white shadow-sm">
              <FaLock size={30} />
            </div>
          </div>
        );
      }

      return (
        <div className="flex w-full items-center justify-between gap-4 rounded-2xl border border-[#C9D8FF] bg-[#EEF3FF] px-5 py-4 text-[#000097] shadow-sm transition hover:border-[#7AA7FF] hover:bg-[#E1EBFF]">
          <div className="min-w-0">
            <p className="text-sm font-extrabold uppercase tracking-wide sm:text-base hover:underline">
              {item.certification.certificate_type === 'renovation'
                ? 'RENOVAÇÃO'
                : 'FAÇA SEU EXAME'}
            </p>
            <p className="mt-1 flex items-center text-sm font-semibold hover:underline">
              <FaArrowAltCircleRight className="mr-2" size={18} /> Realizar
              prova agora
            </p>
          </div>

          <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#000097] text-white shadow-sm">
            <BsFillArrowRightSquareFill size={36} />
          </div>
        </div>
      );
    }

    return (
      <div className="flex w-full items-center justify-between gap-4 rounded-2xl border border-[#C9D8FF] bg-[#EEF3FF] px-5 py-4 text-[#000097] shadow-sm transition hover:border-[#7AA7FF] hover:bg-[#E1EBFF]">
        <div className="min-w-0">
          <p className="text-sm font-extrabold uppercase tracking-wide sm:text-base hover:underline">
            Em Andamento
          </p>
          <p className="mt-1 flex items-center text-sm font-semibold hover:underline">
            <FaArrowAltCircleRight className="mr-2" size={18} /> Continuar
          </p>
        </div>

        <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#000097] text-white shadow-sm">
          <BsFillArrowRightSquareFill size={36} />
        </div>
      </div>
    );
  }

  function getCourseItem(certificationItem: CertificationLog) {
    const _courseItem = course.find(
      courseItem =>
        courseItem.crcpCode === certificationItem.certification.crcp,
    );

    if (
      !_courseItem ||
      !!certificationItem?.extra?.completedCourse ||
      !certificationItem?.extra?.hasCourse ||
      certificationItem?.status !== '1'
    )
      return null;

    return (
      <>
        <div className="mb-5 rounded-2xl border border-[#E2E8F0] bg-[#F8FAFF] p-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <div className="md:w-1/2">
              <b className="block text-sm font-bold text-[#020817] sm:text-base">
                {_courseItem.name}
              </b>
            </div>

            <a
              className="cursor-pointer md:flex md:w-1/2 md:justify-end"
              href={url}
              target="_bank"
              rel="noreferrer"
            >
              <div className="ml-auto flex items-center gap-3">
                <div className="rounded-2xl border border-[#C9D8FF] bg-[#EEF3FF] px-5 py-4 text-[#000097] shadow-sm transition hover:border-[#7AA7FF] hover:bg-[#E1EBFF]">
                  <p className="text-sm font-extrabold uppercase tracking-wide sm:text-base hover:underline">
                    FAÇA SEU CURSO
                  </p>
                  <p className="mt-1 flex items-center text-sm font-semibold hover:underline">
                    <FaArrowAltCircleRight className="mr-2" size={18} /> Ir para
                    o Curso
                  </p>
                </div>

                <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-[#000097] text-white shadow-sm">
                  <BsFillArrowRightSquareFill size={36} />
                </div>
              </div>
            </a>
          </div>
        </div>
        <hr className="border-[#E2E8F0]" />
        <div className="mt-5" />
      </>
    );
  }

  if (isLoading) return <Loading />;

  return (
    <div className="min-h-screen bg-[#F4F7FD] px-4 py-8 sm:px-6">
      <UpdateProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />

      {showEmailGateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="relative w-full max-w-lg rounded-2xl bg-white p-8 shadow-xl">
            <button
              type="button"
              onClick={() => setShowEmailGateModal(false)}
              className="absolute right-4 top-4 text-slate-400 hover:text-slate-600"
              aria-label="Fechar"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <EmailConfirmationGate
              playerId={user?._id ?? ''}
              variant="modal"
              onConfirmed={() => {
                setShowEmailGateModal(false);
              }}
            />
          </div>
        </div>
      )}

      <div className="mx-auto max-w-7xl">
        <div className="grid gap-6 xl:grid-cols-[1.08fr_0.92fr]">
          <section className="min-w-0 rounded-3xl border border-[#E2E8F0] bg-white p-6 shadow-sm sm:p-8">
            <About />
          </section>

          <section className="min-w-0 space-y-5">
            {!certifications?.length ? (
              <div className="rounded-3xl border border-[#E2E8F0] bg-white p-8 shadow-sm">
                <NoData title="Você não possui nenhuma certificação" />
              </div>
            ) : (
              <>
                {isRenew && globalData?.apostila?.url && (
                  <a
                    href={globalData?.apostila?.url}
                    target="_blank"
                    rel="noreferrer"
                    className="block"
                  >
                    <h1 className="mb-3 text-2xl font-bold tracking-tight text-[#020817]">
                      Apostila do Curso
                    </h1>

                    <div className="grid gap-4 rounded-2xl border border-[#C9D8FF] bg-gradient-to-r from-white to-[#EEF3FF] p-6 shadow-sm transition hover:border-[#7AA7FF] hover:shadow-md md:grid-cols-[140px_1fr]">
                      {!!globalData?.capaApostila?.original?.url && (
                        <div className="min-w-[104px]">
                          <img
                            className="h-full w-auto rounded-xl border border-[#E2E8F0]"
                            src={globalData.capaApostila?.original.url}
                            alt=""
                          />
                        </div>
                      )}

                      <div className="flex items-center justify-between gap-4">
                        <p className="block text-justify text-sm leading-relaxed text-[#334155] sm:text-base">
                          <b className="text-base font-bold text-[#020817] sm:text-lg">
                            RENOVAÇÃO - Certificação de Correspondente
                          </b>
                          <br />A nossa apostila está completamente atualizada
                          com os conteúdos necessários para você realizar a
                          renovação da sua certificação com confiança e se
                          manter atualizado para atuar como Correspondente
                          Bancário. Tempo estimado de leitura de 45 min. Bom
                          estudo!
                        </p>

                        <div className="shrink-0">
                          <img
                            className="h-[56px] w-[56px]"
                            src={Download}
                            alt="Ícone de Download"
                          />
                        </div>
                      </div>
                    </div>
                  </a>
                )}

                <div className="rounded-3xl border border-[#E2E8F0] bg-white p-6 shadow-sm sm:p-7">
                  <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                    <h1 className="text-3xl font-bold tracking-tight text-[#020817]">
                      Minhas inscrições
                    </h1>
                    {isFetching && (
                      <span className="rounded-full border border-[#C9D8FF] bg-[#EEF3FF] px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#000097]">
                        Atualizando
                      </span>
                    )}
                  </div>

                  <div className="space-y-4">
                    {certifications.length > 0 &&
                      certifications.map(item => (
                        <div
                          key={item._id}
                          className="overflow-hidden rounded-2xl border border-[#E2E8F0] bg-white p-5 shadow-sm transition hover:border-[#C9D8FF] hover:shadow-md"
                        >
                          {getCourseItem(item)}

                          <div className="flex min-w-0 flex-col gap-5">
                            <div className="min-w-0 space-y-2">
                              <b
                                className="block max-w-full whitespace-normal text-base font-bold leading-snug text-[#020817] sm:text-lg"
                                title={`${
                                  getCertificationType[
                                    item.certification.certificate_type
                                  ]
                                } - ${item.certification.title}`}
                              >
                                {
                                  getCertificationType[
                                    item.certification.certificate_type
                                  ]
                                }{' '}
                                - {item.certification.title}
                              </b>

                              {item?.certification?.voucher_validity &&
                                !item.finished && (
                                  <span className="block text-sm text-[#334155]">
                                    Válido até:{' '}
                                    <b className="font-semibold text-[#020817]">
                                      {handleDate(
                                        item.created,
                                        item.certification?.voucher_validity,
                                      )}
                                    </b>
                                  </span>
                                )}

                              {item.finished && (
                                <span className="block text-sm text-[#334155]">
                                  Prova realizada em:{' '}
                                  <b className="font-semibold text-[#020817]">
                                    {new Date(
                                      item.finished,
                                    ).toLocaleDateString()}{' '}
                                    {new Date(
                                      item.finished,
                                    ).toLocaleTimeString()}
                                  </b>
                                </span>
                              )}
                            </div>

                            <div className="border-t border-[#E2E8F0] pt-4">
                              {item.status === '1' &&
                              verifyValidate(
                                item.created,
                                item.certification?.voucher_validity,
                              ) ? (
                                <div className="w-full">
                                  {item &&
                                  item.extra &&
                                  item.extra.hasCourse &&
                                  !item.extra.completedCourse ? (
                                    handleGoingStatus(item)
                                  ) : (
                                    <button
                                      type="button"
                                      disabled={isCheckingEmail}
                                      className="block w-full cursor-pointer border-0 bg-transparent p-0 text-left disabled:cursor-wait disabled:opacity-70"
                                      onClick={() => handleStartExam(item._id)}
                                      aria-label="Ir para o exame"
                                    >
                                      {handleGoingStatus(item)}
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <div className="w-full">
                                  {handleStatus(item)}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
      <Certificate log={log} clearLog={() => setLog(null)} />
    </div>
  );
}
